import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx } from '@emotion/react';
import { math, stripUnit, remToPx } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';
import { devWarning, utilStyles, ZINDEX_MODAL, isDocumentRTL, hasWindow, uniqueId, isTouchDevice } from '@paypalcorp/pp-react-utils';
import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Animator } from '@paypalcorp/pp-react-animator';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import { FocusOn, AutoFocusInside } from 'react-focus-on';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { CloseIcon } from '@paypalcorp/pp-react-icons';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20, _templateObject21, _templateObject22, _templateObject23, _templateObject24, _templateObject25, _templateObject26, _templateObject27, _templateObject28, _templateObject29, _templateObject30, _templateObject31, _templateObject32, _templateObject33, _templateObject34, _templateObject35, _templateObject36, _templateObject37, _templateObject38, _templateObject39, _templateObject40, _templateObject41, _templateObject42, _templateObject43, _templateObject44, _templateObject45, _templateObject46;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceLg = tokens.sysSpaceLg,
      sysTypographyDisplaySize = tokens.sysTypographyDisplaySize,
      sysSizeIconLg = tokens.sysSizeIconLg,
      sysSizeTapTargetDefault = tokens.sysSizeTapTargetDefault,
      sysSpaceXl = tokens.sysSpaceXl,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSizeMediaMd = tokens.sysSizeMediaMd,
      sysSizeMediaSm = tokens.sysSizeMediaSm,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysSizeMediaLg = tokens.sysSizeMediaLg,
      sysMotionDurationDepart = tokens.sysMotionDurationDepart,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceXs = tokens.sysSpaceXs,
      sysMotionDurationArrive = tokens.sysMotionDurationArrive,
      sysSizeIconSm = tokens.sysSizeIconSm,
      sysSizeIconMd = tokens.sysSizeIconMd,
      sysColorOverlayMainHover = tokens.sysColorOverlayMainHover,
      sysSizeIndicatorMd = tokens.sysSizeIndicatorMd,
      sysSizeIconXl = tokens.sysSizeIconXl,
      sysSpaceJumbo = tokens.sysSpaceJumbo,
      sysSizeMediaXl = tokens.sysSizeMediaXl,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysBorderRadiusLg = tokens.sysBorderRadiusLg,
      sysSizeIndicatorSm = tokens.sysSizeIndicatorSm;

  var _utilStyles = utilStyles(tokens),
      elevationStyles = _utilStyles.elevationStyles,
      variables = _utilStyles.variables,
      screenReader = _utilStyles.screenReader;

  var ppuiEaseInOut = variables.ppuiEaseInOut;

  var _textStyles = textStyles(tokens),
      textBody = _textStyles.body;

  var lightboxMinHeight = math("8 * ".concat(sysSpaceLg));
  var modalHeaderMinHeight = sysTypographyDisplaySize;
  var modalHeaderZoneMinWidth = sysSizeIconLg;
  var dialogWidth = '75%'; // 8 columns in a 12 column grid

  var dialogMaxWidth = math("".concat(sysSizeMediaXl, " * 0.75")); // 75% of XL screen grid max width

  var mdMarginDelta = math("".concat(sysSpaceXl, " * 2"));
  var smMarginDelta = math("".concat(sysSpaceLg, " * 2"));
  var sheetHeaderHeightMobile = math("".concat(sysSizeIconLg, " + (2 * ").concat(sysSpaceXs, ")"));
  var sheetHeaderHeightDesktop = math("".concat(sysSizeIconXl, " + (2 * ").concat(sysSpaceXs, ")"));
  var sheetWidthSm = '100vw';
  var sheetButtonSize = sysSizeIconSm + sysSpaceMd * 2;
  var modalBorderRadius = sysBorderRadiusLg;

  var containerCenter = function () {
    var valign = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'center';
    return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: container_center;\n    display: flex;\n    align-items: ", ";\n    justify-content: space-around;\n  "])), valign);
  };

  var containerMiddle = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: container_middle;\n    ", ";\n    width: ", ";\n    max-width: ", ";\n\n    @media screen and (max-width: ", ") {\n      width: calc(100% - ", ");\n    }\n\n    @media screen and (max-width: ", ") {\n      width: calc(100% - ", ");\n    }\n  "])), containerCenter(), dialogWidth, dialogMaxWidth, sysSizeMediaMd, mdMarginDelta, sysSizeMediaSm, smMarginDelta);
  var containerBottom = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: container_bottom;\n    ", ";\n    width: ", ";\n\n    @media screen and (max-width: ", ") {\n      width: ", ";\n    }\n    @media screen and (max-width: ", ") {\n      width: ", ";\n    }\n  "])), containerCenter('flex-end'), sysSizeMediaMd, sysSizeMediaLg, sysSizeMediaSm, sysSizeMediaMd, sheetWidthSm);
  var base = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: base;\n    background-color: ", ";\n    display: grid;\n    grid-template-rows: auto 1fr auto;\n    position: relative;\n    flex-basis: 100%;\n  "])), sysColorBackgroundSurface);
  var modalNoContainer = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: modal_no_container;\n    width: ", ";\n    max-width: ", ";\n  "])), dialogWidth, dialogMaxWidth);
  var modalDialog = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: modal_dialog;\n    min-height: ", ";\n    border-radius: ", ";\n    max-height: ", ";\n    ", ";\n    opacity: 0;\n    will-change: opacity;\n    transition: opacity ", " ", ";\n  "])), lightboxMinHeight, modalBorderRadius, '75dvh', elevationStyles.level3, sysMotionDurationDepart, ppuiEaseInOut);
  var modalTopBorder = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: modal_top_border;\n    border-top-left-radius: ", ";\n    border-top-right-radius: ", ";\n  "])), modalBorderRadius, modalBorderRadius);
  var modalSheet = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: modal_sheet;\n    height: 100dvh;\n    width: auto;\n    ", ";\n    will-change: transform;\n    transition: transform ", " ", ",\n      height ", " ", ";\n    transform: translateZ(0) translateY(100%);\n\n    @media screen and (max-width: ", ") {\n      width: ", ";\n      height: auto;\n      min-height: ", ";\n      max-height: ", "; /* Fallback for browsers that do not support Custom Properties */\n      ", "\n    }\n  "])), elevationStyles.level3, sysMotionDurationDepart, ppuiEaseInOut, sysMotionDurationDepart, ppuiEaseInOut, sysSizeMediaMd, sheetWidthSm, sysSizeTapTargetDefault, '90dvh', modalTopBorder);
  var modalSheetShow = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: modal_sheet_show;\n    transform: translateZ(0) translateY(0);\n  "])));
  var modalBackdrop = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: modal_backdrop;\n    position: fixed;\n    top: 0;\n    left: 0;\n    bottom: 0;\n    right: 0;\n    z-index: ", ";\n    visibility: hidden;\n    opacity: 0;\n    will-change: opacity;\n    transition: opacity ", " ease-in-out,\n      visibility ", " ", ";\n    background-color: ", ";\n    ", ";\n    box-shadow: none;\n\n    &[aria-hidden='false'] {\n      transition-duration: ", ";\n    }\n\n    &[aria-hidden='true'] {\n      transition-duration: ", ";\n    }\n  "])), ZINDEX_MODAL, sysMotionDurationDepart, sysMotionDurationDepart, sysMotionDurationDepart, sysColorOverlayMainHover, containerCenter('center'), sysMotionDurationArrive, sysMotionDurationArrive);
  var modalBackdropShow = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: modal_backdrop_show;\n    visibility: visible;\n    opacity: 1;\n    transition: opacity ", " ease-in-out;\n  "])), sysMotionDurationDepart);
  var modalBackdropShowDialog = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: modal_backdrop_show_dialog;\n    opacity: 1;\n    transition: opacity ", " ease-in-out\n      ", ";\n  "])), sysMotionDurationDepart, sysMotionDurationDepart);
  var scrim = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: scrim;\n    position: fixed;\n    top: 0;\n    left: 0;\n    bottom: 0;\n    right: 0;\n    z-index: ", ";\n    visibility: hidden;\n    transition: visibility ", " ", ";\n    ", ";\n\n    &::before {\n      content: ' ';\n      position: absolute;\n      top: 0;\n      left: 0;\n      bottom: 0;\n      right: 0;\n      opacity: 0;\n      will-change: opacity;\n      transition: opacity ", " ease-in-out;\n      background-color: ", ";\n    }\n  "])), ZINDEX_MODAL, sysMotionDurationDepart, sysMotionDurationDepart, containerCenter('flex-end'), sysMotionDurationDepart, sysColorOverlayMainHover);
  var scrimShow = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: scrim_show;\n    visibility: visible;\n    transition: none; /* visibility should not be animated when coming in */\n\n    &::before {\n      opacity: 1;\n      transition-duration: ", ";\n    }\n  "])), sysMotionDurationArrive);
  var showGripper = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: show_gripper;\n    &::before {\n      content: ' ';\n      position: absolute;\n      top: ", ";\n      left: 0;\n      right: 0;\n      margin: auto;\n      width: ", ";\n      height: ", ";\n      background-color: ", ";\n      border-radius: ", ";\n      display: none;\n      @media screen and (max-width: ", ") {\n        display: block;\n      }\n    }\n  "])), sysSpaceSm, sysSizeTapTargetDefault, sysSizeIndicatorSm, sysColorBackgroundMediumContrast, modalBorderRadius, sysSizeMediaMd);
  var modalContents = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: modal_contents;\n    flex: 1 1 auto;\n    overflow: auto;\n\n    position: relative;\n    border-radius: inherit;\n    ", ";\n\n    @media screen and (max-width: ", ") {\n      padding: 0 ", " ", ";\n    }\n  "])), textBody(), sysSizeMediaMd, sysSpaceXl, sysSpaceXl);
  var modalContentWithHeader = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    label: modal_content_with_header;\n    @media screen and (max-width: ", ") {\n      border-radius: 0;\n    }\n  "])), sysSizeMediaMd);
  var modalContentsSheet = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: modal_content_sheet;\n    padding: 0 ", ";\n\n    @media screen and (max-width: ", ") {\n      padding: 0 ", ";\n    }\n    @media screen and (max-width: ", ") {\n      padding: 0 ", " ", ";\n    }\n  "])), '8.5rem', sysSizeMediaLg, '5rem', sysSizeMediaMd, sysSpaceLg, sysSpaceLg);
  var modalContentsDialog = css(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    label: modal_content_dialog;\n    max-height: ", ";\n    padding: 0 ", ";\n  "])), '70dvh', sysSpaceJumbo);
  var modalContentsNoPadding = css(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n    label: modal_content_no_padding;\n    padding: 0;\n    @media screen and (max-width: ", ") {\n      padding: 0;\n    }\n  "])), sysSizeMediaLg);
  var modalContentDialogPadding = css(_templateObject21 || (_templateObject21 = _taggedTemplateLiteral(["\n    label: modal_content_dialog_no_padding;\n    @media screen and (max-width: ", ") {\n      padding: 0 ", " ", ";\n    }\n  "])), sysSizeMediaSm, sysSpaceLg, sysSpaceXl);
  var modalContentsCenter = css(_templateObject22 || (_templateObject22 = _taggedTemplateLiteral(["\n    label: modal_content_center;\n    text-align: center;\n  "])));
  var modalHeader = css(_templateObject23 || (_templateObject23 = _taggedTemplateLiteral(["\n    label: modal_header;\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n    flex-wrap: wrap;\n    position: relative;\n    flex: 0 0 auto;\n    z-index: 1;\n    min-height: ", ";\n    padding: ", ";\n  "])), modalHeaderMinHeight, sysSpaceMd);
  var modalHeaderSheet = css(_templateObject24 || (_templateObject24 = _taggedTemplateLiteral(["\n    label: modal_header_sheet;\n    min-height: ", ";\n\n    @media screen and (max-width: ", ") {\n      min-height: ", ";\n      ", "\n    }\n  "])), sheetHeaderHeightDesktop, sysSizeMediaMd, sheetHeaderHeightMobile, modalTopBorder);
  var modalHeaderSheetAction = css(_templateObject25 || (_templateObject25 = _taggedTemplateLiteral(["\n    label: modal_header_sheet_action;\n    min-height: ", ";\n    padding: ", ";\n    margin: 0;\n\n    @media screen and (max-width: ", ") {\n      min-height: ", ";\n      padding: ", ";\n      margin: 0;\n    }\n  "])), sheetHeaderHeightDesktop, sysSpaceMd, sysSizeMediaMd, sheetHeaderHeightMobile, sysSpaceSm);
  var modalHeaderScrolled = css(_templateObject26 || (_templateObject26 = _taggedTemplateLiteral(["\n    label: modal_header_scrolled;\n    ", "\n  "])), elevationStyles.level3);
  var modalHeaderAction = css(_templateObject27 || (_templateObject27 = _taggedTemplateLiteral(["\n    label: modal_header_action;\n    flex: 0 0 ", ";\n    flex-basis: ", ";\n  "])), modalHeaderZoneMinWidth, sheetButtonSize);
  var modalHeaderTitle = css(_templateObject28 || (_templateObject28 = _taggedTemplateLiteral(["\n    label: modal_header_title;\n    flex: 1 1 3rem;\n    margin: ", ";\n    text-align: center;\n    ", ";\n  "])), sysSpaceSm, textBody());
  var modalHeaderClose = css(_templateObject29 || (_templateObject29 = _taggedTemplateLiteral(["\n    label: modal_header_close;\n    flex: 0 0 ", ";\n    text-align: right;\n    flex-basis: ", ";\n  "])), modalHeaderZoneMinWidth, sheetButtonSize);
  var modalHeaderWorkArea = css(_templateObject30 || (_templateObject30 = _taggedTemplateLiteral(["\n    label: modal_header_work_area;\n    flex: 0 0 100%;\n    padding: ", " ", ";\n  "])), sysSpaceSm, sysSpaceLg);
  var modalHeaderLogo = css(_templateObject31 || (_templateObject31 = _taggedTemplateLiteral(["\n    label: modal_header_logo;\n    display: flex;\n    flex: 1 1 3rem;\n    justify-content: center;\n    margin-top: ", ";\n  "])), sysSpaceSm);
  var modalHeaderLogoImage = css(_templateObject32 || (_templateObject32 = _taggedTemplateLiteral(["\n    label: modal_header_logo_image;\n    width: ", ";\n    height: ", ";\n  "])), sysSizeIconMd, sysSizeIconMd);
  var modalContentsClose = css(_templateObject33 || (_templateObject33 = _taggedTemplateLiteral(["\n    label: modal_content_close;\n    position: absolute;\n    right: ", ";\n    top: ", ";\n    z-index: 2;\n\n    [dir='rtl'] & {\n      left: ", ";\n      right: auto;\n\n      @media screen and (min-width: ", ") {\n        left: ", ";\n      }\n    }\n\n    @media screen and (min-width: ", ") {\n      right: ", ";\n      top: ", ";\n    }\n  "])), sysSpaceXs, sysSpaceXs, sysSpaceXs, sysSizeMediaMd, sysSizeIndicatorMd, sysSizeMediaMd, sysSizeIndicatorMd, sysSpaceMd);
  var modalFooter = css(_templateObject34 || (_templateObject34 = _taggedTemplateLiteral(["\n    label: modal_footer;\n    min-height: ", ";\n    padding: ", ";\n    position: relative;\n    flex: 1 0 auto;\n  "])), modalHeaderMinHeight, sysSpaceLg);
  var modalFooterScrolled = css(_templateObject35 || (_templateObject35 = _taggedTemplateLiteral(["\n    label: modal_footer_scrolled;\n    ", "\n    border-bottom-left-radius: inherit;\n    border-bottom-right-radius: inherit;\n  "])), elevationStyles.level3Above);
  var modalFooterAlignCenter = css(_templateObject36 || (_templateObject36 = _taggedTemplateLiteral(["\n    label: modal_footer_align_center;\n    text-align: center;\n  "])));
  var modalFooterAlignRight = css(_templateObject37 || (_templateObject37 = _taggedTemplateLiteral(["\n    label: modal_footer_align_right;\n    text-align: right;\n  "])));
  var modalSheetHeight90 = css(_templateObject38 || (_templateObject38 = _taggedTemplateLiteral(["\n    label: modal_sheet_height_90;\n    @media screen and (max-width: ", ") {\n      height: 90dvh;\n      ", "\n    }\n  "])), sysSizeMediaMd, modalTopBorder);
  var modalSheetHeight100 = css(_templateObject39 || (_templateObject39 = _taggedTemplateLiteral(["\n    label: modal_sheet_height_100;\n    @media screen and (max-width: ", ") {\n      height: 100dvh;\n      max-height: 100dvh;\n      border-top-left-radius: 0;\n      border-top-right-radius: 0;\n    }\n  "])), sysSizeMediaMd);
  var justifyRight = css(_templateObject40 || (_templateObject40 = _taggedTemplateLiteral(["\n    label: sheet_right_side;\n    @media screen and (min-width: ", ") {\n      justify-content: flex-end;\n    }\n  "])), sysSizeMediaMd);
  var containerRightWidth = css(_templateObject41 || (_templateObject41 = _taggedTemplateLiteral(["\n    @media screen and (min-width: ", ") {\n      width: ", ";\n    }\n  "])), sysSizeMediaMd, '35.5rem');
  var commonContainerStyles = css(_templateObject42 || (_templateObject42 = _taggedTemplateLiteral(["\n    label: container;\n    @media screen and (min-width: ", ") {\n      will-change: transform;\n      transition: transform ", ";\n      bottom: inherit;\n\n      &[aria-hidden='false'] {\n        transform: translateZ(0) translateX(0);\n      }\n    }\n  "])), sysSizeMediaMd, sysMotionDurationDepart);
  var containerRight = css(_templateObject43 || (_templateObject43 = _taggedTemplateLiteral(["\n    label: container_right;\n    ", "\n    @media screen and (min-width: ", ") {\n      transform: translateZ(0) translateX(100%);\n    }\n  "])), commonContainerStyles, sysSizeMediaMd);
  var containerLeft = css(_templateObject44 || (_templateObject44 = _taggedTemplateLiteral(["\n    label: container_left;\n    ", "\n    @media screen and (min-width: ", ") {\n      transform: translateZ(0) translateX(-100%);\n    }\n  "])), commonContainerStyles, sysSizeMediaMd);
  var drawerContainer = css(_templateObject45 || (_templateObject45 = _taggedTemplateLiteral(["\n    label: drawer_container;\n    @media screen and (min-width: ", ") {\n      padding-left: ", ";\n      padding-right: ", ";\n    }\n  "])), sysSizeMediaMd, math("".concat(sysSpaceXl, " * 2")), math("".concat(sysSpaceXl, " * 2")));
  var modalSheetHeight50 = css(_templateObject46 || (_templateObject46 = _taggedTemplateLiteral(["\n    label: modal_sheet_height_50;\n    @media screen and (max-width: ", ") {\n      height: 50dvh; /* Fallback for browsers that do not support Custom Properties */\n      ", "\n    }\n  "])), sysSizeMediaMd, modalTopBorder);
  return {
    base: base,
    modalContents: modalContents,
    modalContentsCenter: modalContentsCenter,
    modalContentsNoPadding: modalContentsNoPadding,
    modalHeader: modalHeader,
    modalHeaderScrolled: modalHeaderScrolled,
    modalHeaderClose: modalHeaderClose,
    modalHeaderAction: modalHeaderAction,
    modalHeaderTitle: modalHeaderTitle,
    modalHeaderLogo: modalHeaderLogo,
    modalHeaderLogoImage: modalHeaderLogoImage,
    modalFooter: modalFooter,
    modalFooterScrolled: modalFooterScrolled,
    modalFooterAlignCenter: modalFooterAlignCenter,
    modalFooterAlignRight: modalFooterAlignRight,
    screenReader: screenReader,
    modalTopBorder: modalTopBorder,
    // Dialog specific styles
    modalDialog: modalDialog,
    modalBackdrop: modalBackdrop,
    modalBackdropShow: modalBackdropShow,
    modalBackdropShowDialog: modalBackdropShowDialog,
    modalContentsClose: modalContentsClose,
    modalContentsDialog: modalContentsDialog,
    containerMiddle: containerMiddle,
    modalContentDialogPadding: modalContentDialogPadding,
    // Sheet Specific
    modalSheet: modalSheet,
    modalSheetShow: modalSheetShow,
    modalSheetHeight100: modalSheetHeight100,
    modalSheetHeight90: modalSheetHeight90,
    modalSheetHeight50: modalSheetHeight50,
    modalNoContainer: modalNoContainer,
    modalHeaderSheet: modalHeaderSheet,
    modalHeaderSheetAction: modalHeaderSheetAction,
    modalHeaderWorkArea: modalHeaderWorkArea,
    modalContentsSheet: modalContentsSheet,
    modalContentWithHeader: modalContentWithHeader,
    scrim: scrim,
    scrimShow: scrimShow,
    showGripper: showGripper,
    containerBottom: containerBottom,
    justifyRight: justifyRight,
    containerRightWidth: containerRightWidth,
    containerRight: containerRight,
    containerLeft: containerLeft,
    drawerContainer: drawerContainer
  };
}

var _excluded = ["requestClose", "onBeforeClose", "className", "isHidden", "center", "noPadding", "closeButtonLabel", "noCloseButton", "closeButtonProps", "hideTitle", "showLogo", "mobileFixedHeight", "showGripper", "title", "id", "action", "headerContents", "footerContents", "footerAlign", "children", "returnFocusToTrigger", "headerClassName", "containerClassName", "contentContainerRef", "wrapperClassName", "footerClassName", "skipFormFocus", "contentScrollTop", "onContentScroll", "showDrawer"];

var closeSheet = function () {
  return true;
}; // eslint-disable-next-line complexity


var SheetComponent = function (_ref) {
  var requestClose = _ref.requestClose,
      _ref$onBeforeClose = _ref.onBeforeClose,
      onBeforeClose = _ref$onBeforeClose === void 0 ? closeSheet : _ref$onBeforeClose,
      className = _ref.className,
      isHidden = _ref.isHidden,
      center = _ref.center,
      noPadding = _ref.noPadding,
      _ref$closeButtonLabel = _ref.closeButtonLabel,
      closeButtonLabel = _ref$closeButtonLabel === void 0 ? 'close sheet' : _ref$closeButtonLabel,
      noCloseButton = _ref.noCloseButton,
      _ref$closeButtonProps = _ref.closeButtonProps,
      closeButtonProps = _ref$closeButtonProps === void 0 ? {} : _ref$closeButtonProps,
      hideTitle = _ref.hideTitle,
      showLogo = _ref.showLogo,
      mobileFixedHeight = _ref.mobileFixedHeight,
      _ref$showGripper = _ref.showGripper,
      showGripper = _ref$showGripper === void 0 ? false : _ref$showGripper,
      title = _ref.title,
      id = _ref.id,
      action = _ref.action,
      headerContents = _ref.headerContents,
      footerContents = _ref.footerContents,
      footerAlign = _ref.footerAlign,
      children = _ref.children,
      _ref$returnFocusToTri = _ref.returnFocusToTrigger,
      returnFocusToTrigger = _ref$returnFocusToTri === void 0 ? true : _ref$returnFocusToTri,
      headerClassName = _ref.headerClassName,
      containerClassName = _ref.containerClassName,
      contentContainerRef = _ref.contentContainerRef,
      wrapperClassName = _ref.wrapperClassName,
      footerClassName = _ref.footerClassName,
      skipFormFocus = _ref.skipFormFocus,
      _ref$contentScrollTop = _ref.contentScrollTop,
      contentScrollTop = _ref$contentScrollTop === void 0 ? 0 : _ref$contentScrollTop,
      onContentScroll = _ref.onContentScroll,
      _ref$showDrawer = _ref.showDrawer,
      showDrawer = _ref$showDrawer === void 0 ? false : _ref$showDrawer,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      headerScroll = _useState2[0],
      setHeaderScroll = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      footerScroll = _useState4[0],
      setFooterScroll = _useState4[1];

  var _useState5 = useState(0.5),
      _useState6 = _slicedToArray(_useState5, 2),
      dynamicHeight = _useState6[0],
      setDynamicHeight = _useState6[1];

  var sheetRef = useRef();
  var closeButtonRef = useRef();
  var actionZoneRef = useRef();
  var refContent = useRef();
  var contentRef = contentContainerRef || refContent;
  var headerContentRef = useRef();
  var isRtl = isDocumentRTL();
  var startX;
  var startY;
  var isDynamicHeight = mobileFixedHeight === 'dynamic' || showGripper; // If theme is not directly provided, get from themeContext

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var sysSizeMediaMd = tokens.sysSizeMediaMd;
  var MEDIUM_BREAKPOINT = stripUnit(remToPx(sysSizeMediaMd, '16px'));
  /* istanbul ignore next */

  var isMobile = hasWindow() ? window && window.innerWidth < MEDIUM_BREAKPOINT : false;
  var headerLogo = showLogo && !isMobile;
  var idBase = id !== null && id !== void 0 ? id : uniqueId();
  var showHeader = !!action || !noCloseButton || !hideTitle || !!headerContents || headerLogo; // jest has no way to test things like scrollTop or scrollHeight --BCC

  /* istanbul ignore next */

  var scrollCheck = function () {
    if (contentRef && contentRef.current) {
      var contents = contentRef.current;
      var newHeaderScroll = contents.scrollTop !== 0;

      if (newHeaderScroll !== headerScroll) {
        setHeaderScroll(newHeaderScroll);
      } // Footer is optional, may not be one to watch for!


      if (footerContents) {
        // Have we scrolled to the bottom?
        var newFooterScroll = contents.scrollTop + contents.offsetHeight !== contents.scrollHeight;

        if (newFooterScroll !== footerScroll) {
          setFooterScroll(newFooterScroll);
        }
      }
    }
  };

  /* istanbul ignore next */
  var setFixedHeight = function () {
    var sheetEl = sheetRef.current;

    if (sheetEl) {
      var height90Percent = window.innerHeight * 0.9;

      if (mobileFixedHeight === '100') {
        sheetEl.style.height = "".concat(window.innerHeight);
      } else if (mobileFixedHeight === '90') {
        sheetEl.style.height = "".concat(height90Percent, "px");
      } else if (mobileFixedHeight === '50') {
        sheetEl.style.height = "".concat(window.innerHeight * 0.5, "px");
      } else {
        sheetEl.style.maxHeight = "".concat(height90Percent, "px");
      }
    }
  };
  /* istanbul ignore next */


  var updateHeight = function () {
    var sheetEl = sheetRef.current;

    if (sheetEl) {
      sheetEl.style.height = "".concat(window.innerHeight * dynamicHeight, "px");
      sheetEl.style.maxHeight = 'fit-content';
    }
  };
  /* istanbul ignore next */


  var increaseHeight = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              setDynamicHeight(0.9);

            case 1:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function () {
      return _ref2.apply(this, arguments);
    };
  }();
  /* istanbul ignore next */


  var decreaseHeight = function () {
    setDynamicHeight(0.5);
  };
  /* istanbul ignore next */


  var checkContentScroll = function () {
    var content = contentRef && contentRef.current;

    if (dynamicHeight === 0.5) {
      content.style.overflow = 'hidden';
    }

    if (dynamicHeight === 0.9) {
      content.style.overflow = 'scroll';
    }
  };

  useEffect(function () {
    if (!isHidden) {
      var _window;

      scrollCheck();
      /*eslint-disable */
      // istanbul ignore next
      // eslint-disable-next-line no-unused-expressions

      (_window = window) === null || _window === void 0 ? void 0 : _window.setTimeout(function () {
        /*eslint-enable */
        // HACK ALERT! react-on-focus fails at setting the focus
        // if we are transitioning opacity. This causes the sheet
        // to set the focus on the close button but NOT indicate
        // that fact to the user either visually or in the screen reader
        // So timeOut was needed
        var focused = false; // For combobox input on sheet header

        if (headerContentRef && headerContentRef.current) {
          //@ts-ignore
          var formElement = headerContentRef.current.querySelector('input');

          if (formElement) {
            formElement.focus();
            focused = true;
          }
        } // For Forms


        if (!focused && !skipFormFocus && contentRef && contentRef.current) {
          //@ts-ignore
          var focusableEls = contentRef.current.querySelectorAll('a, button, textarea, input, select');

          if (focusableEls.length > 0) {
            focusableEls[0].focus();
            focused = true;
          }
        } // Otherwise focus on action item or close button


        if (!focused && showHeader) {
          if (action && actionZoneRef && actionZoneRef.current) {
            //@ts-ignore
            var focusableActionEl = actionZoneRef.current.querySelectorAll('a, button, select');

            if (focusableActionEl.length > 0) {
              focusableActionEl[0].focus();
            }
          } else {
            var closeButtonCurrent = closeButtonRef.current;

            if (closeButtonCurrent) {
              //@ts-ignore
              closeButtonCurrent.focus();
            }
          }
        }
      }, 500);
      /* istanbul ignore next */

      if (isTouchDevice() && window.innerHeight) {
        window.addEventListener('resize', setFixedHeight, false);

        if (isDynamicHeight) {
          updateHeight();
          checkContentScroll();
        } else {
          setFixedHeight();
        }
      }
    }

    return function () {
      window.removeEventListener('resize', setFixedHeight, false);
    };
  }, [isHidden, dynamicHeight]);
  useEffect(function () {
    if (!isHidden && contentRef.current) {
      var contents = contentRef.current;
      contents.scrollTop = contentScrollTop;
    }
  }, [contentScrollTop, isHidden]);
  var wrapperCss = [_styles.scrim, !isHidden && _styles.scrimShow, showDrawer && _styles.justifyRight];
  var containerCss = [_styles.base, _styles.modalNoContainer, _styles.modalSheet, mobileFixedHeight === '100' && _styles.modalSheetHeight100, mobileFixedHeight === '90' && _styles.modalSheetHeight90, mobileFixedHeight === '50' && _styles.modalSheetHeight50, isDynamicHeight && _styles.modalSheetHeight50, !isHidden && _styles.modalSheetShow, showDrawer && !isRtl && _styles.containerRight, showDrawer && isRtl && _styles.containerLeft];
  var contentCss = [_styles.modalContents, _styles.modalContentsSheet, center && _styles.modalContentsCenter, noPadding && _styles.modalContentsNoPadding, showHeader && _styles.modalContentWithHeader, showDrawer && _styles.drawerContainer];
  var ariaHidden = isHidden ? 'true' : 'false';

  var handleClose = function (evt, closedBy) {
    var okToClose = onBeforeClose(closedBy);

    if (okToClose) {
      requestClose(evt, closedBy);
      startX = null;
      startY = null;
    }
  };

  var closeButtonTag = noCloseButton ? null : jsx(IconButton, _extends({
    svgIconComponent: CloseIcon,
    onClick: function onClick(evt) {
      handleClose(evt, 'closeButton');
    },
    ref: closeButtonRef,
    buttonType: "tertiary"
  }, closeButtonProps, {
    "data-ppui": "true"
  }), closeButtonLabel);

  /* istanbul ignore next */
  var mobileTouchEnd = function (evt, content) {
    var endTouch = evt.changedTouches[0];
    var endX = endTouch.pageX;
    var endY = endTouch.pageY;
    var xDiff = startX - endX;
    var yDiff = startY - endY; // means up/down is most significant

    if (Math.abs(yDiff) > Math.abs(xDiff)) {
      // swipe Down
      if (yDiff < 0 && (content ? content.scrollTop === 0 : true)) {
        if (isDynamicHeight) {
          decreaseHeight();

          if (yDiff < -150 || dynamicHeight === 0.5) {
            handleClose(evt, 'swipeDown');
          }
        } else {
          handleClose(evt, 'swipeDown');
        }
      } // swipe Up


      if (yDiff > 0 && isDynamicHeight) {
        increaseHeight();
      }
    }

    startX = null;
    startY = null;
  };
  /* istanbul ignore next */


  var handleTouchMove = function (evt) {
    // Prevent from scrolling
    evt.preventDefault();
  };
  /* istanbul ignore next */


  var handleTouchStart = function (evt) {
    /* istanbul ignore next */
    if (!startY && !startX) {
      var firstTouch = evt.changedTouches[0];
      startY = firstTouch.pageY;
      startX = firstTouch.pageX;
    }
  };
  /* istanbul ignore next */


  var titleId = "header_".concat(idBase);
  var ariaProps = {
    role: 'dialog',
    className: wrapperClassName,
    'aria-modal': 'true',
    'aria-hidden': ariaHidden
  };

  if (headerLogo || hideTitle) {
    ariaProps['aria-label'] = title;
  } else {
    ariaProps['aria-labelledby'] = titleId;
  } // If there's a close button, we need to have an action zone even if there's nothing in it
  // Same for close button. Need to have an empty div if there's something in the action
  // But if neither is present, we want to give the whole space to the title.


  var emptyCloseButtonZone = action ? jsx("div", {
    css: [_styles.modalHeaderClose],
    "data-ppui": "true"
  }) : null;
  var emptyActionZone = closeButtonTag ? jsx("div", {
    css: [_styles.modalHeaderAction],
    "data-ppui": "true"
  }) : null; // Now that we've decided what the empty states would look like, decide if they're actually empty or not

  var closeButtonZone = closeButtonTag ? jsx("div", {
    css: [_styles.modalHeaderClose],
    "data-ppui": "true"
  }, closeButtonTag) : emptyCloseButtonZone;
  var actionZone = action ? jsx("div", {
    css: [_styles.modalHeaderAction],
    ref: actionZoneRef,
    "data-ppui": "true"
  }, action) : emptyActionZone;
  var titleContentCss = [headerLogo ? _styles.modalHeaderLogo : _styles.modalHeaderTitle];
  var titleContentZone = jsx("div", {
    css: titleContentCss,
    id: titleId,
    "aria-live": "assertive",
    "data-ppui": "true"
  }, function titleContents() {
    if (headerLogo) {
      return jsx("img", {
        src: "https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-mark-color.svg",
        alt: "paypal-logo",
        css: _styles.modalHeaderLogoImage,
        "data-ppui": "true"
      });
    }

    return hideTitle ? null : title;
  }());
  var headerContentZone = headerContents ? jsx("div", {
    css: _styles.modalHeaderWorkArea,
    ref: headerContentRef,
    "data-ppui": "true"
  }, headerContents) : null;
  var header = showHeader ? jsx("header", {
    className: headerClassName,
    css: [_styles.modalHeader, _styles.modalHeaderSheet, headerScroll && _styles.modalHeaderScrolled, actionZone && _styles.modalHeaderSheetAction, showGripper && _styles.showGripper],
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: function handleTouchEnd(evt) {
      mobileTouchEnd(evt, null);
    },
    "data-ppui": "true"
  }, actionZone, titleContentZone, closeButtonZone, headerContentZone) : null;
  var footer = footerContents ? jsx("footer", {
    className: footerClassName,
    css: [_styles.modalFooter,
    /* istanbul ignore next */
    footerScroll && _styles.modalFooterScrolled, footerAlign === 'center' && _styles.modalFooterAlignCenter, footerAlign === 'right' && _styles.modalFooterAlignRight],
    "data-ppui": "true"
  }, footerContents) : null;
  return jsx("div", _extends({}, ariaProps, {
    css: wrapperCss,
    "data-ppui-info": "sheet_3.14.2"
  }), jsx(FocusOn, {
    enabled: !isHidden // @ts-ignore: onEscapeKey() wants an Event, but the rest of this code wants SyntheticEvent<>
    ,
    onEscapeKey: function handleEscape(evt) {
      evt.preventDefault();
      handleClose(evt, 'ESC');
    },
    onClickOutside: function handleBackdropClick(evt) {
      handleClose(evt, 'backdropClick');
    },
    returnFocus: returnFocusToTrigger,
    css: [_styles.containerBottom, showDrawer && _styles.containerRightWidth],
    "data-ppui": "true"
  }, jsx("article", _extends({
    css: containerCss,
    className: containerClassName
  }, rest, {
    ref: sheetRef,
    "aria-hidden": ariaHidden,
    "data-ppui": "true"
  }), header, jsx("section", {
    css: contentCss,
    className: className,
    onScroll: function handleScroll() {
      /* istanbul ignore else */
      if (contentRef && contentRef.current) {
        var contents = contentRef.current;
        /* istanbul ignore else */

        if (onContentScroll) {
          onContentScroll(contents.scrollTop);
        }
      }

      scrollCheck();
    },
    ref: contentRef,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: function handleContentTouchEnd(evt) {
      var content = contentRef && contentRef.current;

      if (isDynamicHeight) {
        mobileTouchEnd(evt, content);
      }
    },
    "data-ppui": "true"
  }, children), footer)));
};

var _excluded$1 = ["isOpen", "onOpen", "onClose", "onCloseComplete", "containerRef"];

var Sheet = function (_ref) {
  var isOpen = _ref.isOpen,
      onOpen = _ref.onOpen,
      onClose = _ref.onClose,
      _ref$onCloseComplete = _ref.onCloseComplete,
      onCloseComplete = _ref$onCloseComplete === void 0 ? onClose : _ref$onCloseComplete,
      containerRef = _ref.containerRef,
      componentProps = _objectWithoutProperties(_ref, _excluded$1);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isMounted = _useState2[0],
      setIsMounted = _useState2[1];

  useEffect(function () {
    setIsMounted(true);
  }, []);
  var contents = /*#__PURE__*/React.createElement(Animator, {
    shouldHide: !isOpen,
    onOpen: onOpen,
    onCloseComplete: onCloseComplete,
    "data-ppui": "true"
  }, function animatee(props) {
    return /*#__PURE__*/React.createElement(SheetComponent, _extends({}, props, componentProps, {
      "data-ppui": "true"
    }));
  });
  var containerElm = containerRef && containerRef.current; // Delay createPortal until component is mounted to fix server side rendering

  return isMounted && ReactDOM.createPortal(contents, containerElm || document.body);
};

var _excluded$2 = ["isFullView", "showInverseCloseButton", "requestClose", "className", "headerClassName", "footerClassName", "containerClassName", "isHidden", "center", "hideTitle", "closeButtonLabel", "closeButtonProps", "closeOnBackgroundClick", "onBeforeClose", "contentContainerRef", "title", "id", "action", "footerContents", "footerAlign", "children", "skipFormFocus", "showTitleInHeader"];

var closeDialog = function () {
  return true;
};

var DialogComponent = function (_ref) {
  var isFullView = _ref.isFullView,
      showInverseCloseButton = _ref.showInverseCloseButton,
      requestClose = _ref.requestClose,
      className = _ref.className,
      headerClassName = _ref.headerClassName,
      footerClassName = _ref.footerClassName,
      containerClassName = _ref.containerClassName,
      isHidden = _ref.isHidden,
      center = _ref.center,
      _ref$hideTitle = _ref.hideTitle,
      hideTitle = _ref$hideTitle === void 0 ? isFullView : _ref$hideTitle,
      _ref$closeButtonLabel = _ref.closeButtonLabel,
      closeButtonLabel = _ref$closeButtonLabel === void 0 ? 'Close Dialog' : _ref$closeButtonLabel,
      _ref$closeButtonProps = _ref.closeButtonProps,
      closeButtonProps = _ref$closeButtonProps === void 0 ? {} : _ref$closeButtonProps,
      closeOnBackgroundClick = _ref.closeOnBackgroundClick,
      _ref$onBeforeClose = _ref.onBeforeClose,
      onBeforeClose = _ref$onBeforeClose === void 0 ? closeDialog : _ref$onBeforeClose,
      contentContainerRef = _ref.contentContainerRef,
      title = _ref.title,
      id = _ref.id,
      action = _ref.action,
      footerContents = _ref.footerContents,
      footerAlign = _ref.footerAlign,
      children = _ref.children,
      skipFormFocus = _ref.skipFormFocus,
      _ref$showTitleInHeade = _ref.showTitleInHeader,
      showTitleInHeader = _ref$showTitleInHeade === void 0 ? false : _ref$showTitleInHeade,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      headerScroll = _useState2[0],
      setHeaderScroll = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      footerScroll = _useState4[0],
      setFooterScroll = _useState4[1];

  var refContent = useRef();
  var contentRef = contentContainerRef || refContent;
  var closeButtonRef = useRef();
  var actionZoneRef = useRef();
  var idBase = id || uniqueId(); // jest has no way to test things like scrollTop or scrollHeight --BCC

  /* istanbul ignore next */

  var scrollCheck = function () {
    if (contentRef && contentRef.current) {
      var contents = contentRef.current;
      var newHeaderScroll = contents.scrollTop !== 0;

      if (newHeaderScroll !== headerScroll) {
        setHeaderScroll(newHeaderScroll);
      } // Footer is optional, may not be one to watch for!


      if (footerContents) {
        // Have we scrolled to the bottom?
        var newFooterScroll = Math.ceil(contents.scrollTop + contents.getBoundingClientRect().height) < contents.scrollHeight;

        if (newFooterScroll !== footerScroll) {
          setFooterScroll(newFooterScroll);
        }
      }
    }
  };

  useEffect(function () {
    if (!isHidden) {
      scrollCheck(); // istanbul ignore next

      if (window && window.setTimeout) {
        // istanbul ignore next
        window.setTimeout(function () {
          // HACK ALERT! react-on-focus fails at setting the focus
          // if we are transitioning opacity. This causes the dialog
          // to set the focus on the close button but NOT indicate
          // that fact to the user either visually or in the screen reader
          // So timeOut was needed
          var focused = false; // For Forms

          if (!focused && !skipFormFocus && contentRef && contentRef.current) {
            //@ts-ignore
            var focusableEls = contentRef.current.querySelectorAll('a, button, textarea, input, select');

            if (focusableEls.length > 0) {
              focusableEls[0].focus();
              focused = true;
            }
          } // Otherwise focus on action item or close button


          if (!focused && (!!action || !hideTitle)) {
            if (action && actionZoneRef && actionZoneRef.current) {
              //@ts-ignore
              var focusableActionEl = actionZoneRef.current.querySelectorAll('a, button, select');

              if (focusableActionEl.length > 0) {
                focusableActionEl[0].focus();
              }
            } else {
              var closeButton = closeButtonRef.current;

              if (closeButton) {
                //@ts-ignore
                closeButton.focus();
              }
            }
          }
        }, 100);
      }
    }
  }, [isHidden]);
  /* istanbul ignore next */

  // If theme is not directly provided, get from themeContext
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var wrapperCss = [_styles.modalBackdrop, !isHidden && _styles.modalBackdropShow];
  var contentCss = [_styles.modalContents, _styles.modalContentsDialog, center && _styles.modalContentsCenter, isFullView && _styles.modalContentsNoPadding, !isFullView && _styles.modalContentDialogPadding];

  var handleClose = function (evt, closedBy) {
    var okToClose = onBeforeClose(closedBy);
    /* istanbul ignore next */

    if (okToClose) {
      requestClose(evt, closedBy);
    }
  };

  var titleId = "header_".concat(idBase);
  var titleElement = null;
  var ariaProps = {
    role: 'dialog',
    'aria-modal': 'true',
    'aria-hidden': isHidden ? 'true' : 'false'
  };

  if (hideTitle || showTitleInHeader) {
    ariaProps['aria-label'] = title;

    if (showTitleInHeader && !isFullView) {
      titleElement = jsx("span", {
        id: titleId,
        "aria-live": "assertive",
        "data-ppui": "true"
      }, title);
    }
  } else {
    ariaProps['aria-labelledby'] = titleId;
    titleElement = jsx("h2", {
      id: titleId,
      "aria-live": "assertive",
      "data-ppui": "true"
    }, title);
  }

  var closeButtonContainerCss = isFullView ? _styles.modalContentsClose : _styles.modalHeaderClose;
  var closeButtonTag = jsx("div", {
    css: closeButtonContainerCss,
    "data-ppui": "true"
  }, jsx(IconButton, _extends({
    buttonType: "tertiary",
    svgIconComponent: CloseIcon,
    inverse: showInverseCloseButton,
    ref: closeButtonRef,
    onClick: function handleCloseButtonClick(evt) {
      handleClose(evt, 'closeButton');
    }
  }, closeButtonProps, {
    "data-ppui": "true"
  }), closeButtonLabel));
  var footer = footerContents ? jsx("footer", {
    className: footerClassName,
    css: [_styles.modalFooter,
    /* istanbul ignore next */
    footerScroll && _styles.modalFooterScrolled, footerAlign === 'center' && _styles.modalFooterAlignCenter, footerAlign === 'right' && _styles.modalFooterAlignRight],
    "data-ppui": "true"
  }, footerContents) : null;
  var header = jsx("header", {
    className: headerClassName,
    css: [_styles.modalHeader,
    /* istanbul ignore next */
    headerScroll && _styles.modalHeaderScrolled, _styles.modalTopBorder],
    "data-ppui": "true"
  }, jsx("div", {
    css: _styles.modalHeaderAction,
    ref: actionZoneRef,
    "data-ppui": "true"
  }, action), jsx("div", {
    css: _styles.modalHeaderTitle,
    "data-ppui": "true"
  }, showTitleInHeader && titleElement), closeButtonTag);
  return jsx("div", _extends({}, ariaProps, {
    css: wrapperCss,
    "data-ppui-info": "dialog_3.14.2"
  }), jsx(FocusOn, {
    enabled: !isHidden // @ts-ignore: onEscapeKey() wants an Event, but the rest of this code wants SyntheticEvent<>
    ,
    onEscapeKey: function handleEscape(evt) {
      evt.preventDefault();
      handleClose(evt, 'ESC');
    },
    onClickOutside: function handleBackdropClick(evt) {
      if (closeOnBackgroundClick) {
        handleClose(evt, 'backdropClick');
      }
    },
    css: _styles.containerMiddle,
    "data-ppui": "true"
  }, jsx("article", _extends({
    css: [_styles.base, _styles.modalDialog, !isHidden && _styles.modalBackdropShowDialog],
    className: containerClassName
  }, rest, {
    "data-ppui": "true"
  }), isFullView ? closeButtonTag : header, jsx("section", {
    className: className,
    css: contentCss,
    onScroll: function scrollChecker() {
      return scrollCheck();
    },
    ref: contentRef,
    "data-ppui": "true"
  }, jsx(AutoFocusInside, {
    "data-ppui": "true"
  }, !showTitleInHeader && titleElement, children)), footer)));
};

var _excluded$3 = ["isOpen", "onOpen", "onClose", "onCloseComplete", "containerRef"];

var Dialog = function (_ref) {
  var isOpen = _ref.isOpen,
      onOpen = _ref.onOpen,
      onClose = _ref.onClose,
      _ref$onCloseComplete = _ref.onCloseComplete,
      onCloseComplete = _ref$onCloseComplete === void 0 ? onClose : _ref$onCloseComplete,
      containerRef = _ref.containerRef,
      componentProps = _objectWithoutProperties(_ref, _excluded$3);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isMounted = _useState2[0],
      setIsMounted = _useState2[1];

  useEffect(function () {
    setIsMounted(true);
  }, []);
  var contents = /*#__PURE__*/React.createElement(Animator, {
    shouldHide: !isOpen,
    onOpen: onOpen,
    onCloseComplete: onCloseComplete,
    "data-ppui": "true"
  }, function animatee(props) {
    return /*#__PURE__*/React.createElement(DialogComponent, _extends({}, props, componentProps, {
      "data-ppui": "true"
    }));
  });
  var containerElm = containerRef && containerRef.current; // Delay createPortal until component is mounted to fix server side rendering

  return isMounted && ReactDOM.createPortal(contents, containerElm || document.body);
};

export { Dialog, Sheet, styles as modalStyles };
